import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarDays, Info, Phone, Users } from "lucide-react";
import { getConfig, getFleet, mediaUrl } from "../lib/api";
import { locationPhone } from "../lib/content";
import { useHours } from "../hooks/useHours";
import BoatTypeGraphic from "./BoatTypeGraphic";
import BookingOutage from "./BookingOutage";
import Reveal from "./Reveal";

const money = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : null;

const Skeleton = () => (
  <div data-testid="fleet-card-skeleton" className="overflow-hidden rounded-2xl border border-sand-border bg-white">
    <div className="w-full animate-pulse bg-sand-card" style={{ aspectRatio: "16 / 9" }} />
    <div className="space-y-3 p-5">
      <div className="h-3 w-24 animate-pulse rounded bg-sand-card" />
      <div className="h-5 w-40 animate-pulse rounded bg-sand-card" />
      <div className="h-9 w-full animate-pulse rounded-full bg-sand-card" />
    </div>
  </div>
);

const LocationSelect = ({ options, value, onChange }) => (
  <label className="mt-8 flex w-fit items-center gap-2 text-xs font-semibold text-slate-600">
    Location
    <select
      data-testid="fleet-location-select"
      value={value ?? ""}
      onChange={(e) => onChange(Number(e.target.value))}
      className="rounded-full border border-navy/15 bg-white px-3 py-1.5 text-xs font-bold text-navy"
    >
      {options.map((l) => (
        <option key={l.locationId} value={l.locationId}>
          {l.name}
        </option>
      ))}
    </select>
  </label>
);

export const FleetStrip = ({
  locationId,
  slug: pageSlug,
  date,
  onChangeDate,
  heading,
  intro,
  showLocationPicker = true,
  selectedSlug,
  onSelectSlug,
}) => {
  const { locations } = useHours();
  const options = (locations || []).filter((l) => l.enabled && l.locationId != null);
  const [picked, setPicked] = useState(null);
  useEffect(() => setPicked(null), [locationId]);
  const chosenId = options.find((l) => l.slug === selectedSlug)?.locationId ?? picked;
  const activeId = chosenId ?? locationId ?? options[0]?.locationId ?? null;
  const activeLoc = options.find((l) => l.locationId === activeId);
  const slug = activeLoc?.slug ?? pageSlug;
  const { phone, href: phoneHref } = locationPhone(activeLoc, slug) || {};
  const pickLocation = (id) =>
    onSelectSlug ? onSelectSlug(options.find((l) => l.locationId === id)?.slug) : setPicked(id);
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const reqRef = useRef(0);
  const [showDegraded, setShowDegraded] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [sort, setSort] = useState("recommended");

  useEffect(() => {
    getConfig()
      .then((c) => setShowDegraded(Boolean(c.showDegradedBanner)))
      .catch(() => setShowDegraded(false));
  }, []);

  useEffect(() => {
    if (!date || activeId == null) return;
    const token = ++reqRef.current;
    setState({ loading: true, data: null, error: null });
    getFleet(activeId, date)
      .then((d) => {
        if (token === reqRef.current) setState({ loading: false, data: d, error: null });
      })
      .catch((e) => {
        if (token === reqRef.current) {
          setState({ loading: false, data: null, error: e.message || "Unable to load boats" });
        }
      });
  }, [activeId, date]);

  const allUnits = (state.data?.units || []).filter((u) => u.bookable);
  const degraded = state.data?.degraded || [];

  // Type filter + sort are derived from live data only: an option never appears
  // for a category the marina isn't actually renting that day.
  const types = [];
  for (const u of allUnits) {
    const t = u.assetTypeName || "Other";
    const hit = types.find((x) => x.name === t);
    if (hit) hit.count += 1;
    else types.push({ name: t, count: 1 });
  }
  types.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const activeType = types.some((t) => t.name === typeFilter) ? typeFilter : "all";
  const units = [...allUnits]
    .filter((u) => activeType === "all" || (u.assetTypeName || "Other") === activeType)
    .sort((a, b) => {
      if (sort === "price-asc") return (a.fromAmount ?? 1e9) - (b.fromAmount ?? 1e9);
      if (sort === "price-desc") return (b.fromAmount ?? -1) - (a.fromAmount ?? -1);
      if (sort === "capacity-desc") return (b.capacity ?? 0) - (a.capacity ?? 0);
      // Default: biggest boats first within type order, which is how the marina sells.
      return (
        types.findIndex((t) => t.name === (a.assetTypeName || "Other")) -
          types.findIndex((t) => t.name === (b.assetTypeName || "Other")) ||
        (b.capacity ?? 0) - (a.capacity ?? 0)
      );
    });
  // An empty fleet because availability itself failed is an OUTAGE, not a fully
  // booked day. Saying "no boats available" there would be dishonest.
  const outage = Boolean(state.error) || degraded.includes("availability");

  return (
    <section data-testid="fleet-strip" className="mx-auto max-w-7xl px-5 lg:px-10 py-16 lg:py-24">
      <Reveal className="max-w-2xl">
        <p className="eyebrow text-lake">Live availability</p>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
          {heading || "Our fleet on the water"}
        </h2>
        <p className="mt-4 text-base text-slate-700 leading-relaxed">
          {intro ||
            "Every boat below is pulled live from our booking system for the date you picked — no stock listings, no guesswork."}
        </p>
      </Reveal>

      {showDegraded && degraded.length > 0 && (
        <div
          data-testid="degraded-banner"
          role="status"
          className="mt-8 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
          <p className="text-sm text-amber-900">
            <strong className="font-bold">Internal notice:</strong> some booking data is unavailable right now (
            {degraded.join(", ")}). Boat photos and specifications are hidden until it recovers.
          </p>
        </div>
      )}

      {showLocationPicker && options.length > 1 && (
        <LocationSelect options={options} value={activeId} onChange={pickLocation} />
      )}

      <div className="mt-10">
        {state.loading && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </div>
        )}

        {!state.loading && outage && (
          <BookingOutage testId="fleet-outage-state" />
        )}

        {!state.loading && !outage && units.length === 0 && (
          <div
            data-testid="fleet-empty-state"
            className="rounded-2xl border border-sand-border bg-white p-8 text-center"
          >
            <CalendarDays className="mx-auto h-8 w-8 text-lake" aria-hidden="true" />
            <p className="mt-4 font-display text-xl font-bold text-navy">No boats available on this date</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
              Nothing is bookable for {date}. Try another date, or give us a call and we'll find you something.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              {onChangeDate && (
                <button
                  type="button"
                  data-testid="fleet-try-another-date-button"
                  onClick={onChangeDate}
                  className="inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-bold text-white hover:bg-ember-hover transition-colors"
                >
                  <CalendarDays className="h-4 w-4" aria-hidden="true" />
                  Try another date
                </button>
              )}
              {phone && (
                <a
                  href={phoneHref}
                  data-testid="fleet-empty-call-link"
                  className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-6 py-3 text-sm font-bold text-navy hover:bg-sand-card transition-colors"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  Call {phone}
                </a>
              )}
            </div>
          </div>
        )}

        {!state.loading && !outage && units.length > 0 && (
          <>
            <div
              data-testid="fleet-filter-bar"
              className="mb-8 flex flex-col gap-4 border-b border-sand-border pb-5 lg:flex-row lg:items-center lg:justify-between"
            >
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  data-testid="fleet-filter-all"
                  onClick={() => setTypeFilter("all")}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                    activeType === "all"
                      ? "bg-navy text-white"
                      : "border border-navy/15 text-navy hover:border-lake"
                  }`}
                >
                  All boats ({allUnits.length})
                </button>
                {types.map((t) => (
                  <button
                    key={t.name}
                    type="button"
                    data-testid={`fleet-filter-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setTypeFilter(t.name)}
                    className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                      activeType === t.name
                        ? "bg-navy text-white"
                        : "border border-navy/15 text-navy hover:border-lake"
                    }`}
                  >
                    {t.name} ({t.count})
                  </button>
                ))}
              </div>
              <label className="flex shrink-0 items-center gap-2 text-xs font-semibold text-slate-600">
                Sort
                <select
                  data-testid="fleet-sort-select"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="rounded-full border border-navy/15 bg-white px-3 py-1.5 text-xs font-bold text-navy"
                >
                  <option value="recommended">Recommended</option>
                  <option value="price-asc">Price: low to high</option>
                  <option value="price-desc">Price: high to low</option>
                  <option value="capacity-desc">Most passengers</option>
                </select>
              </label>
            </div>

            <div data-testid="fleet-grid" className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {units.map((u, i) => (
                <Reveal
                  key={u.unitKey}
                  delay={i * 70}
                  data-testid={`fleet-card-${u.unitKey}`}
                  className="group overflow-hidden rounded-2xl border border-sand-border bg-white transition-shadow hover:shadow-xl hover:shadow-navy/10"
                >
                  {u.heroImageUrl ? (
                    <img
                      src={mediaUrl(u.heroImageUrl)}
                      alt={`${u.displayName}${u.assetTypeName ? `, ${u.assetTypeName}` : ""}`}
                      loading="lazy"
                      width={640}
                      height={360}
                      className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      style={{ aspectRatio: "16 / 9" }}
                    />
                  ) : (
                    <BoatTypeGraphic typeName={u.assetTypeName} />
                  )}
                  <div className="p-5">
                    <p className="eyebrow text-lake">{u.assetTypeName || "Watercraft"}</p>
                    <h3 className="mt-2 font-display text-xl font-semibold text-navy">{u.displayName}</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-slate-500">
                      {u.capacity ? (
                        <span data-testid={`fleet-card-capacity-${u.unitKey}`} className="inline-flex items-center gap-1">
                          <Users className="h-3.5 w-3.5 text-lake" aria-hidden="true" />
                          Up to {u.capacity} people
                        </span>
                      ) : null}
                      {/* Genuine remaining capacity from the vendor when it is
                          available; otherwise the hull count from availability. */}
                      {(u.remainingCount ?? u.availableCount) > 1 && (
                        <span data-testid={`fleet-card-count-${u.unitKey}`}>
                          {u.remainingCount ?? u.availableCount} available
                        </span>
                      )}
                    </div>
                    <div className="mt-4 flex items-end justify-between gap-3">
                      <div>
                        {money(u.fromAmount) ? (
                          <>
                            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">from</span>
                            <p
                              data-testid={`fleet-card-price-${u.unitKey}`}
                              className="font-display text-2xl font-bold text-navy leading-none"
                            >
                              {money(u.fromAmount)}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-slate-500">Call for pricing</p>
                        )}
                      </div>
                      <span className="rounded-full bg-lake/10 px-3 py-1 text-xs font-bold text-lake">
                        {u.serviceName}
                      </span>
                    </div>
                    <Link
                      to={`/boat-rentals/${slug}/boats/${u.assetId}?date=${date}`}
                      data-testid={`fleet-card-details-${u.unitKey}`}
                      className="mt-5 block w-full rounded-full bg-navy px-4 py-2.5 text-center text-sm font-bold text-white transition-colors hover:bg-navy-surface"
                    >
                      View boat &amp; times
                    </Link>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="mt-6 flex items-start gap-2 text-xs text-slate-500">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              Showing boats bookable on {date}. Starting prices are the lowest rate that applies to that day — durations,
              departure times and add-ons are chosen on the boat's page.
            </p>
          </>
        )}
      </div>
    </section>
  );
};

export default FleetStrip;
